import React, { useEffect } from 'react';
import closeModal from 'Helpers/ModalHelper';
import useCollection from 'Hooks/useCollection';
import { makeStyles } from '@material-ui/core';
import MetaCollectionFormDialog from 'Components/UI/Meta/MetaForm/MetaCollectionFormDialog';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { QueryVal, removeQuery } from 'Helpers/Query';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import typeMetaOverrides from 'Overrides/typeMetaOverrides';
import {
  displaySuccess,
  displayErrorMessage,
} from 'Helpers/FacetNotificationManager';
import typeLayoutOverrides from 'Overrides/typeLayoutOverrides';
import useMobileLayout from 'Hooks/useMobileLayout';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: theme.dimensions.xl,
    maxWidth: theme.dimensions.xl,
  },
}));

function LinkModal({
  editTitle,
  createTitle,
  link,
  onSubmit,
  metaOverride,
  // We need this to lookup the meta override set, but it is not
  // currently returned with an empty collection
  type,
  ...rest
}) {
  const { collection, setCollection } = useCollection();
  const isMobile = useMobileLayout();
  const classes = useStyles();
  const linkUrl = QueryVal(link);

  const handleClose = () => {
    closeModal();
    removeQuery(link);
  };

  const loadCollection = async () => {
    if (!link) {
      return;
    }

    if (!metaOverride && !(type in typeMetaOverrides)) {
      throw new Error(`Resource type ${type} not in type meta overrides`);
    }

    try {
      const coll = await fr.http.get(linkUrl);
      if (coll.resources().length() === 0) {
        coll.create({
          addToCollection: true,
        });
      }

      let collectionWithOverride = coll;
      const metaOverrideSet = metaOverride || typeMetaOverrides[type];
      if (metaOverrideSet) {
        collectionWithOverride = coll.overrideMeta(metaOverrideSet);
      }
      setCollection(collectionWithOverride);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  useEffect(() => {
    loadCollection();
  }, [linkUrl]);

  if (!collection) {
    return <LoadingIndicator />;
  }

  const layouts = typeLayoutOverrides[type];
  let layout;
  if (isMobile && layouts.mobileLayout) {
    layout = layouts.mobileLayout;
  } else {
    layout = layouts.desktopLayout;
  }

  const onSuccess = () => {
    displaySuccess('Save successful');
    if (onSubmit) {
      onSubmit();
    }
    handleClose();
  };

  const onFailure = (err) => {
    displayErrorMessage(err, err.errors);
  };

  const editMode = !!collection.resources().head().link('self');
  const title = editMode ? editTitle : createTitle;
  return (
    <MetaCollectionFormDialog
      classes={{
        dialogPaper: classes.dialogPaper,
      }}
      onClose={handleClose}
      onFailure={onFailure}
      onSubmit={onSuccess}
      collection={collection}
      title={typeof title === 'function' ? title(collection) : title}
      rows={layout}
      {...rest}
    />
  );
}

LinkModal.defaultProps = {
  onSubmit: () => {},
  metaOverride: null,
};

LinkModal.propTypes = {
  link: PropTypes.string.isRequired,
  createTitle: PropTypes.string.isRequired,
  editTitle: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  metaOverride: PropTypes.object,
};

export default LinkModal;
